import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, Users, ShoppingCart, Heart, Award, Target, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import ProductCarousel from '@/components/ProductCarousel';
import ProductSections from '@/components/products/ProductSections';
import MissionStats from '@/components/MissionStats';
import Footer from '@/components/Footer';

const Index = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<string | null>(null);
    const [currentProduct, setCurrentProduct] = useState(0);

  // Metallic text styling
  const metallicTextStyle = {
    background: 'linear-gradient(135deg, #949598 0%, #c1c5c9 25%, #949598 50%, #e8ebef 75%, #949598 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    textShadow: '0 1px 3px rgba(0,0,0,0.3)',
    filter: 'drop-shadow(0 0 8px rgba(148, 149, 152, 0.3))'
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add paddle cursor effect for the entire page
  useEffect(() => {
    if (isMobile) return;

    const paddle = document.createElement('div');
    paddle.id = 'cursorPaddle';
    paddle.className = 'fixed top-0 left-0 w-11 h-11 pointer-events-none z-50';
    paddle.innerHTML = `
      <svg
        viewBox="0 0 44 44"
        fill="#C7FF00"
        stroke="#000"
        stroke-width="1.5"
        class="w-full h-full drop-shadow-[0_0_2px_#C7FF00]"
      >
        <rect x="8" y="4" width="28" height="28" rx="6"></rect>
        <rect x="18" y="32" width="8" height="10" rx="2"></rect>
      </svg>
    `;
    document.body.appendChild(paddle);

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    
    let x = 0, y = 0, tx = 0, ty = 0, prevX = 0, prevY = 0, angle = 0;
    const FOLLOW = 0.35;

    const handlePointerMove = (e: PointerEvent) => {
      tx = e.clientX;
      ty = e.clientY;
      document.body.style.cursor = 'none';
      paddle.style.opacity = '1';
    };

    const handleMouseLeave = () => {
      document.body.style.cursor = 'auto';
      paddle.style.opacity = '0';
    };

    function loop() {
      x = lerp(x, tx, FOLLOW);
      y = lerp(y, ty, FOLLOW);

      const dx = x - prevX;
      const dy = y - prevY;
      prevX = x;
      prevY = y;
      if (dx * dx + dy * dy > 0.001) {
        angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      }

      paddle.style.transform = `translate(${x - 22}px, ${y - 22}px) rotate(${angle}deg)`;
      
      requestAnimationFrame(loop);
    }

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('mouseleave', handleMouseLeave);
    loop();

    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.body.style.cursor = 'auto';
      if (document.body.contains(paddle)) {
        document.body.removeChild(paddle);
      }
    };
  }, [isMobile]);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <Navigation />
      
      {/* Hero Section */}
      <Hero />
      
      {/* Enhanced Product Showcase */}
      <ProductSections />
      
      {/* Early Bird Offer */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-red-600/20 via-lime-400/20 to-red-600/20 border border-red-500/30 rounded-2xl p-6 mb-16 text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-black animate-pulse">🔴 LIVE</span>
              <span className="text-red-400 font-bold text-lg uppercase tracking-wide">FLASH SALE ACTIVE</span>
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-black animate-pulse">🔴 LIVE</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-white mb-4">
              EARLY BIRD PRICING - 30% OFF EVERYTHING
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Limited time founder's pricing. Once we hit 1,000 customers, prices go up. <br />
              <span className="text-lime-400 font-bold"> Currently at 847 customers.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
              <div className="bg-red-500/20 rounded-lg px-4 py-2 border border-red-500/30">
                <span className="text-red-400 text-sm font-bold">⏰ 153 spots remaining</span>
              </div>
              <div className="bg-lime-400/20 rounded-lg px-4 py-2 border border-lime-400/30">
                <span className="text-lime-400 text-sm font-bold">🚚 Free shipping included</span>
              </div>
            </div>
          </div>
              </div>
      </section>

      {/* Why Athletes Choose PBX */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-900 via-black to-gray-900">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-black text-white mb-8 uppercase tracking-tight">
              Why Athletes Choose PBX
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-lime-400/10 to-black border border-lime-400/20 rounded-2xl p-6">
                <div className="text-4xl mb-4">🏆</div>
                <div className="text-lime-400 font-bold text-sm uppercase mb-2">Adaptive-First</div>
                <div className="text-gray-300 text-sm">Designed with wheelchair athletes from day one</div>
              </div>
              <div className="bg-gradient-to-br from-purple-600/10 to-black border border-purple-600/20 rounded-2xl p-6">
                <div className="text-4xl mb-4">⚡</div>
                <div className="text-purple-400 font-bold text-sm uppercase mb-2">Performance</div>
                <div className="text-gray-300 text-sm">Professional-grade materials and construction</div>
              </div>
              <div className="bg-gradient-to-br from-lime-400/10 to-black border border-lime-400/20 rounded-2xl p-6">
                <div className="text-4xl mb-4">🇺🇸</div>
                <div className="text-lime-400 font-bold text-sm uppercase mb-2">American Made</div>
                <div className="text-gray-300 text-sm">Proudly crafted in Chicago, Illinois</div>
              </div>
              <div className="bg-gradient-to-br from-purple-600/10 to-black border border-purple-600/20 rounded-2xl p-6">
                <div className="text-4xl mb-4">💚</div>
                <div className="text-purple-400 font-bold text-sm uppercase mb-2">Purpose-Driven</div>
                <div className="text-gray-300 text-sm">10% of sales support adaptive sports</div>
              </div>
            </div>
          </div>
        </div>
      </section>

          {/* Customer Reviews Section */}
      <section className="py-16 sm:py-20 bg-black">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h3 className="text-3xl sm:text-4xl font-black text-white mb-8 text-center uppercase tracking-tight">
              ATHLETE REVIEWS
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-lime-400/10 to-black border border-lime-400/20 rounded-2xl p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-lime-400 text-lg">⭐⭐⭐⭐⭐</div>
                  <span className="ml-2 text-gray-400 text-sm">Verified Purchase</span>
                </div>
                <p className="text-white text-sm mb-4 leading-relaxed">
                  "As a wheelchair player, finding gear that actually works for me has been impossible. 
                  The adaptive closures on these shirts are game-changing. Finally feel confident on court."
                </p>
                <div className="text-lime-400 font-bold text-sm">— Marcus J., Paralympic Athlete</div>
                </div>
              <div className="bg-gradient-to-br from-purple-600/10 to-black border border-purple-600/20 rounded-2xl p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-purple-400 text-lg">⭐⭐⭐⭐⭐</div>
                  <span className="ml-2 text-gray-400 text-sm">Verified Purchase</span>
                </div>
                <p className="text-white text-sm mb-4 leading-relaxed">
                  "The hoodie quality is insane - feels like a $200+ piece but adaptive features make it priceless. 
                  Moisture-wicking works perfectly during intense training sessions."
                </p>
                <div className="text-purple-400 font-bold text-sm">— Sarah K., Division 1 Coach</div>
              </div>
              <div className="bg-gradient-to-br from-lime-400/10 to-black border border-lime-400/20 rounded-2xl p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-lime-400 text-lg">⭐⭐⭐⭐⭐</div>
                  <span className="ml-2 text-gray-400 text-sm">Verified Purchase</span>
                </div>
                <p className="text-white text-sm mb-4 leading-relaxed">
                  "Finally, someone who gets it. These aren't just adaptive clothes - they're premium athletic wear 
                  that happens to work perfectly for people like me. Style + function = perfection."
                </p>
                <div className="text-lime-400 font-bold text-sm">— Alex R., Adaptive Athlete</div>
              </div>
                </div>
              </div>
              </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-gradient-to-r from-lime-400/10 to-purple-600/10 border border-lime-400/20 rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl sm:text-4xl font-black text-white mb-6 uppercase">
                Ready to Elevate Your Game?
              </h3>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Join 2,500+ athletes who've upgraded to PBX. Free shipping, 30-day returns, and a lifetime commitment to inclusive sport.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button className="bg-lime-400 text-black hover:bg-lime-300 font-black px-12 py-5 rounded-2xl text-xl transition-all duration-300 hover:shadow-xl hover:shadow-lime-400/25 hover:scale-105">
                  SHOP FULL COLLECTION
                  <ShoppingCart className="ml-3 w-6 h-6" />
                </Button>
                <Button variant="outline" className="border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-bold px-12 py-5 rounded-2xl text-xl transition-all duration-300">
                  SIZE & FIT GUIDE
              </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Stats */}
      <MissionStats />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Index;
